import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import AppNavbar from '../components/AppNavbar';
import BottomNav from '../components/BottomNav';

// ── tipos ─────────────────────────────────────────────────────────────
interface Mensaje {
  contenido: string;
  adjunto: string;
  fecha_hora: string;
  idtecnico: number;
}

interface Ticket {
  id: number;
  idcliente: number;
  asunto: string;
  fecha_soporte: string;
  estado: 'abierto' | 'cerrado' | 'respondido';
  fecha_cerrado: string;
  solicitante: string;
  fechavisita: string;
  turno: string;
  agendado: string;
  dp: string;
  lastdate: string;
  motivo_cierre: string;
  mensajes: Mensaje[];
}

interface ResumenTickets {
  abiertos: number;
  cerrados: number;
  respondidos: number;
  respuesta_cliente: number;
}

// ── helpers ───────────────────────────────────────────────────────────
const decodeHTML = (html: string): string => {
  if (!html) return '';
  // Primero decodificar entidades (&lt; &gt; &amp; &nbsp; etc.)
  const txt = document.createElement('textarea');
  txt.innerHTML = html;
  let decoded = txt.value;
  // Luego eliminar etiquetas HTML reales
  decoded = decoded.replace(/<br\s*\/?>/gi, '\n');
  decoded = decoded.replace(/<\/p>/gi, '\n');
  decoded = decoded.replace(/<[^>]+>/g, '');
  decoded = decoded.replace(/ /g, ' ');
  return decoded.trim();
};
// Color solido del estado (barra lateral, punto, cifra)
const estadoColor: Record<string, string> = {
  abierto: 'var(--sn-exito)',
  respondido: 'var(--sn-marca)',
  cerrado: 'var(--sn-texto-3)',
};

// Fondo tenue del estado (chips y badges)
const estadoFondo: Record<string, string> = {
  abierto: 'var(--sn-exito-suave)',
  respondido: 'var(--sn-marca-suave)',
  cerrado: 'var(--sn-superficie-3)',
};

// Texto que va ENCIMA del fondo tenue del estado
const estadoTexto: Record<string, string> = {
  abierto: 'var(--sn-exito-texto)',
  respondido: 'var(--sn-marca-texto)',
  cerrado: 'var(--sn-texto-2)',
};

const estadoLabel: Record<string, string> = {
  abierto: 'Abierto',
  respondido: 'Respondido',
  cerrado: 'Cerrado',
};

const dpColor: Record<string, string> = {
  'Soporte técnico': 'var(--sn-info-suave)',
  'Instalaciones': 'var(--sn-exito-suave)',
  'Empresarial': 'var(--sn-alerta-suave)',
  'Ventas': 'var(--sn-marca-suave)',
  'Quejas y Sugerencias': 'var(--sn-peligro-suave)',
  'Pago por partes': 'var(--sn-superficie-3)',
};

const fechaRelativa = (fecha: string): string => {
  if (!fecha || fecha.startsWith('0000')) return '';
  const diff = Date.now() - new Date(fecha).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `hace ${mins} min`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `hace ${hrs}h`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `hace ${days}d`;
  return new Date(fecha).toLocaleDateString('es-EC', { day: '2-digit', month: 'short', year: '2-digit' });
};

const fmtFecha = (fecha: string): string => {
  if (!fecha || fecha.startsWith('0000')) return '';
  return new Date(fecha).toLocaleDateString('es-EC', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

// ── componente principal ──────────────────────────────────────────────
const Tickets: React.FC = () => {
  const navigate = useNavigate();
  const clientData = (() => { try { return JSON.parse(localStorage.getItem('clientData') || '{}'); } catch { return {}; } })();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [resumen, setResumen] = useState<ResumenTickets | null>(null);
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [filtro, setFiltro] = useState<'todos' | 'abierto' | 'respondido' | 'cerrado'>('todos');
  const [busqueda, setBusqueda] = useState('');
  const [ticketSeleccionado, setTicketSeleccionado] = useState<Ticket | null>(null);

  // ── CAPTCHA para cerrados ─────────────────────────────────────────────
  const [cerradosDesbloqueados, setCerradosDesbloqueados] = useState(false);
  const [showCaptcha, setShowCaptcha] = useState(false);
  const [captchaError, setCaptchaError] = useState(false);
  const [captcha, setCaptcha] = useState({ emoji: '', a: 0, b: 0, opciones: [] as number[], respuesta: 0 });

  const EMOJIS = ['🍎', '🌟', '🐶', '🍕', '🎯', '🦋', '🍦', '⚽', '🌈', '🎸'];

  const generarCaptcha = () => {
    const emoji = EMOJIS[Math.floor(Math.random() * EMOJIS.length)];
    const a = Math.floor(Math.random() * 5) + 1;
    const b = Math.floor(Math.random() * 5) + 1;
    const respuesta = a + b;
    const wrongOptions = new Set<number>();
    while (wrongOptions.size < 2) {
      const w = Math.floor(Math.random() * 10) + 1;
      if (w !== respuesta) wrongOptions.add(w);
    }
    const opciones = [respuesta, ...Array.from(wrongOptions)].sort(() => Math.random() - 0.5);
    setCaptcha({ emoji, a, b, opciones, respuesta });
    setCaptchaError(false);
  };

  const abrirCaptcha = () => {
    generarCaptcha();
    setShowCaptcha(true);
  };

  const responderCaptcha = (opcion: number) => {
    if (opcion === captcha.respuesta) {
      setShowCaptcha(false);
      setCerradosDesbloqueados(true);
      setFiltro('cerrado');
      setCaptchaError(false);
    } else {
      setCaptchaError(true);
      setTimeout(() => generarCaptcha(), 800);
    }
  };

  useEffect(() => {
    cargarTickets();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cargarTickets = async () => {
    setLoading(true);
    setError('');
    try {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 10000);
      const res = await fetch(process.env.REACT_APP_API_URL_TICKETS!, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token: clientData.token_cliente,
          idsoporte: clientData.id_tecnico,
        }),
        signal: controller.signal,
      });
      clearTimeout(timer);
      const json = await res.json();
      if (json?.estado === 'exito') {
        setResumen({
          abiertos: json.data.abiertos,
          cerrados: json.data.cerrados,
          respondidos: json.data.respondidos,
          respuesta_cliente: json.data.respuesta_cliente,
        });
        setTickets(json.data.tickets || []);
      } else {
        setError('No se pudieron cargar los tickets.');
      }
    } catch (e: any) {
      setError(e?.name === 'AbortError'
        ? '⏱️ El servidor tardó demasiado. Intenta de nuevo.'
        : '❌ Error al conectar. Verifica tu conexión.');
    } finally {
      setLoading(false);
    }
  };

  const ticketsFiltrados = tickets.filter(t => {
    if (t.estado === 'cerrado' && !cerradosDesbloqueados) return false;
    const coincideFiltro = filtro === 'todos' || t.estado === filtro;
    const coincideBusqueda = t.asunto.toLowerCase().includes(busqueda.toLowerCase()) ||
      t.solicitante.toLowerCase().includes(busqueda.toLowerCase()) ||
      t.dp.toLowerCase().includes(busqueda.toLowerCase());
    return coincideFiltro && coincideBusqueda;
  });

  // ── vista detalle ──────────────────────────────────────────────────
  if (ticketSeleccionado) {
    const t = ticketSeleccionado;
    const color = estadoColor[t.estado] || 'var(--sn-texto-3)';
    const fondoEstado = estadoFondo[t.estado] || 'var(--sn-superficie-3)';
    const textoEstado = estadoTexto[t.estado] || 'var(--sn-texto-2)';
    const dpBg = dpColor[t.dp] || 'var(--sn-superficie-2)';
    const estadoIcon: Record<string, string> = { abierto: '🟢', respondido: '🔵', cerrado: '⚫' };

    const InfoFila = ({ icono, label, valor, colorValor }: { icono: string; label: string; valor: string; colorValor?: string }) => (
      valor ? (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '9px 0', borderBottom: '1px solid var(--sn-borde)' }}>
          <span style={{ fontSize: 16, width: 22, textAlign: 'center', flexShrink: 0, marginTop: 1 }}>{icono}</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 10, color: 'var(--sn-texto-3)', textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: colorValor || 'var(--sn-texto)', marginTop: 1, wordBreak: 'break-word' }}>{valor}</div>
          </div>
        </div>
      ) : null
    );

    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden', fontFamily: 'Arial, sans-serif' }}>
      <AppNavbar />
      <div style={{ flex: 1, overflowY: 'auto' as const, background: 'var(--sn-fondo)' }}>

        {/* Header sticky */}
        <div style={{
          background: 'var(--sn-accion)', color: 'var(--sn-accion-texto)', padding: '14px 16px',
          display: 'flex', alignItems: 'center', gap: 12,
          position: 'sticky', top: 0, zIndex: 10,
          boxShadow: 'var(--sn-sombra)',
        }}>
          <button onClick={() => setTicketSeleccionado(null)} style={{
            background: 'rgba(255,255,255,0.15)', border: 'none', color: 'var(--sn-accion-texto)',
            borderRadius: 8, padding: '5px 12px', cursor: 'pointer', fontSize: 18, lineHeight: 1,
          }}>←</button>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 11, opacity: 0.65 }}>Detalle del ticket</div>
            <div style={{ fontWeight: 700, fontSize: 15 }}>#{t.id}</div>
          </div>
          <span style={{
            background: color, color: 'var(--sn-texto-invertido)', fontSize: 11, fontWeight: 700,
            padding: '4px 12px', borderRadius: 20,
          }}>
            {estadoIcon[t.estado]} {estadoLabel[t.estado]}
          </span>
        </div>

        {/* Hero — asunto + departamento */}
        <div style={{
          background: 'var(--sn-accion)', padding: '0 16px 20px',
        }}>
          <div style={{
            background: 'rgba(255,255,255,0.08)', borderRadius: 14, padding: '14px 16px',
          }}>
            <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.6)', marginBottom: 4 }}>ASUNTO</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--sn-accion-texto)', lineHeight: 1.4 }}>{t.asunto}</div>
            <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
              <span style={{ background: dpBg, color: 'var(--sn-texto)', fontSize: 10, fontWeight: 700, padding: '3px 10px', borderRadius: 20 }}>
                {t.dp}
              </span>
              <span style={{ background: fondoEstado, color: textoEstado, fontSize: 10, fontWeight: 700, padding: '3px 10px', borderRadius: 20 }}>
                {estadoIcon[t.estado]} {estadoLabel[t.estado]}
              </span>
              <span style={{ background: 'rgba(255,255,255,0.12)', color: 'var(--sn-accion-texto)', fontSize: 10, padding: '3px 10px', borderRadius: 20 }}>
                #{t.id}
              </span>
            </div>
          </div>
        </div>

        <div style={{ padding: '14px 16px 30px' }}>

          {/* Tarjeta detalles */}
          <div style={{ background: 'var(--sn-superficie)', borderRadius: 16, padding: '4px 16px', marginBottom: 14, boxShadow: 'var(--sn-sombra)' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--sn-marca-texto)', padding: '12px 0 4px', textTransform: 'uppercase', letterSpacing: 0.8 }}>
              📋 Información del ticket
            </div>
            <InfoFila icono="👤" label="Solicitante" valor={t.solicitante || 'Sin especificar'} />
            <InfoFila icono="🏢" label="Departamento" valor={t.dp} colorValor="var(--sn-marca-texto)" />
            <InfoFila icono="📅" label="Fecha de creación" valor={fmtFecha(t.fecha_soporte)} />
            <InfoFila icono="🗓️" label="Fecha de visita" valor={t.fechavisita && !t.fechavisita.startsWith('0000') ? `${t.fechavisita}${t.turno || ''}` : ''} />
            <InfoFila icono="📍" label="Agendado en" valor={t.agendado || ''} />
            <InfoFila icono="⏱️" label="Última actividad" valor={fechaRelativa(t.lastdate)} />
            {t.fecha_cerrado && !t.fecha_cerrado.startsWith('0000') && (
              <InfoFila icono="✅" label="Fecha de cierre" valor={fmtFecha(t.fecha_cerrado)} colorValor="var(--sn-exito)" />
            )}
            {t.motivo_cierre ? (
              <InfoFila icono="📝" label="Motivo de cierre" valor={t.motivo_cierre} />
            ) : null}
            <div style={{ paddingBottom: 6 }} />
          </div>

          {/* Estadísticas rápidas */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
            <div style={{ background: 'var(--sn-superficie)', borderRadius: 14, padding: '12px 14px', boxShadow: 'var(--sn-sombra)', textAlign: 'center' }}>
              <div style={{ fontSize: 26, fontWeight: 800, color: 'var(--sn-marca-texto)' }}>{t.mensajes.length}</div>
              <div style={{ fontSize: 11, color: 'var(--sn-texto-3)' }}>Mensajes</div>
            </div>
            <div style={{ background: 'var(--sn-superficie)', borderRadius: 14, padding: '12px 14px', boxShadow: 'var(--sn-sombra)', textAlign: 'center' }}>
              <div style={{ fontSize: 26, fontWeight: 800, color: 'var(--sn-alerta)' }}>
                {t.mensajes.filter(m => m.adjunto).length}
              </div>
              <div style={{ fontSize: 11, color: 'var(--sn-texto-3)' }}>Adjuntos</div>
            </div>
          </div>

          {/* Historial de mensajes */}
          <div style={{ background: 'var(--sn-superficie)', borderRadius: 16, overflow: 'hidden', boxShadow: 'var(--sn-sombra)' }}>
            <div style={{
              padding: '12px 16px', background: 'var(--sn-superficie-2)',
              borderBottom: '1px solid var(--sn-borde)', display: 'flex', alignItems: 'center', gap: 8,
            }}>
              <span style={{ fontSize: 15 }}>💬</span>
              <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--sn-marca-texto)', textTransform: 'uppercase', letterSpacing: 0.6 }}>
                Historial de mensajes
              </span>
              <span style={{
                marginLeft: 'auto', background: 'var(--sn-marca)', color: 'var(--sn-texto-invertido)',
                fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20,
              }}>
                {t.mensajes.length}
              </span>
            </div>

            {t.mensajes.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '30px 0', color: 'var(--sn-texto-3)' }}>
                <div style={{ fontSize: 36 }}>📭</div>
                <p style={{ marginTop: 8, fontSize: 13 }}>Sin mensajes aún</p>
              </div>
            ) : (
              <div style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 12 }}>
                {t.mensajes.map((m, i) => {
                  const esTecnico = m.idtecnico > 0;
                  const texto = decodeHTML(m.contenido);
                  const nombreAdjunto = m.adjunto
                    ? (m.adjunto.includes('_') ? m.adjunto.split('_').slice(1).join('_') : m.adjunto)
                    : '';
                  const esImagen = nombreAdjunto.match(/\.(jpg|jpeg|png|gif|webp)$/i);
                  return (
                    <div key={i} style={{
                      display: 'flex', flexDirection: 'column',
                      alignItems: esTecnico ? 'flex-end' : 'flex-start',
                    }}>
                      {/* Avatar + nombre */}
                      <div style={{
                        display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4,
                        flexDirection: esTecnico ? 'row-reverse' : 'row',
                      }}>
                        <div style={{
                          width: 28, height: 28, borderRadius: '50%', display: 'flex',
                          alignItems: 'center', justifyContent: 'center', fontSize: 13,
                          background: esTecnico ? 'var(--sn-accion)' : 'var(--sn-superficie-3)', flexShrink: 0,
                        }}>
                          {esTecnico ? '🔧' : '👤'}
                        </div>
                        <div style={{ textAlign: esTecnico ? 'right' : 'left' }}>
                          <div style={{ fontSize: 11, fontWeight: 700, color: esTecnico ? 'var(--sn-marca-texto)' : 'var(--sn-texto-2)' }}>
                            {esTecnico ? `Técnico #${m.idtecnico}` : 'Solicitante'}
                          </div>
                          <div style={{ fontSize: 10, color: 'var(--sn-texto-3)' }}>{fmtFecha(m.fecha_hora)}</div>
                        </div>
                      </div>

                      {/* Burbuja */}
                      <div style={{
                        maxWidth: '85%',
                        background: esTecnico ? 'var(--sn-accion)' : 'var(--sn-marca-suave)',
                        color: esTecnico ? 'var(--sn-accion-texto)' : 'var(--sn-texto)',
                        padding: '10px 14px',
                        borderRadius: esTecnico ? '16px 4px 16px 16px' : '4px 16px 16px 16px',
                        boxShadow: 'var(--sn-sombra)',
                        fontSize: 13, lineHeight: 1.6,
                        whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                      }}>
                        {texto || <span style={{ opacity: 0.5, fontStyle: 'italic' }}>Sin contenido</span>}

                        {/* Adjunto */}
                        {m.adjunto && (
                          <div style={{
                            marginTop: 8, padding: '8px 12px',
                            background: esTecnico ? 'rgba(255,255,255,0.12)' : 'var(--sn-superficie)',
                            borderRadius: 10, display: 'flex', alignItems: 'center', gap: 8,
                            border: `1px solid ${esTecnico ? 'rgba(255,255,255,0.2)' : 'var(--sn-borde)'}`,
                          }}>
                            <span style={{ fontSize: 20, flexShrink: 0 }}>
                              {esImagen ? '🖼️' : '📄'}
                            </span>
                            <div style={{ minWidth: 0 }}>
                              <div style={{ fontSize: 11, fontWeight: 600, color: esTecnico ? 'var(--sn-accion-texto)' : 'var(--sn-marca-texto)',
                                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 200 }}>
                                {nombreAdjunto}
                              </div>
                              <div style={{ fontSize: 10, color: esTecnico ? 'rgba(255,255,255,0.6)' : 'var(--sn-texto-3)' }}>
                                {esImagen ? 'Imagen adjunta' : 'Archivo adjunto'}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Número de mensaje */}
                      <div style={{ fontSize: 9, color: 'var(--sn-texto-3)', marginTop: 2 }}>
                        Mensaje {i + 1} de {t.mensajes.length}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

        </div>
      </div>
      <BottomNav />
      </div>
    );
  }

  // ── vista lista ────────────────────────────────────────────────────
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden', fontFamily: 'Arial, sans-serif' }}>
      <AppNavbar />
      <div style={{ flex: 1, overflowY: 'auto' as const, background: 'var(--sn-fondo)' }}>
      {/* Header */}
      <div style={{
        background: 'var(--sn-accion)', color: 'var(--sn-accion-texto)', padding: '14px 16px',
        display: 'flex', alignItems: 'center', gap: 12,
        position: 'sticky', top: 0, zIndex: 10,
      }}>
        <button onClick={() => navigate('/menu')} style={{
          background: 'rgba(255,255,255,0.15)', border: 'none', color: 'var(--sn-accion-texto)',
          borderRadius: 8, padding: '4px 10px', cursor: 'pointer', fontSize: 18,
        }}>←</button>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 17 }}>Mis Tickets</div>
          <div style={{ fontSize: 11, opacity: 0.7 }}>{clientData.nombre}</div>
        </div>
        <button onClick={cargarTickets} style={{
          background: 'rgba(255,255,255,0.15)', border: 'none', color: 'var(--sn-accion-texto)',
          borderRadius: 8, padding: '6px 10px', cursor: 'pointer', fontSize: 16,
        }}>🔄</button>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px 0' }}>
          <Spinner animation="border" style={{ color: 'var(--sn-marca)' }} />
          <p style={{ marginTop: 12, color: 'var(--sn-texto-2)' }}>Cargando tickets...</p>
        </div>
      ) : error ? (
        <div style={{ margin: 20, background: 'var(--sn-peligro-suave)', borderRadius: 12, padding: 20, textAlign: 'center' }}>
          <div style={{ fontSize: 40 }}>⚠️</div>
          <p style={{ color: 'var(--sn-peligro-texto)', marginTop: 8 }}>{error}</p>
          <button onClick={cargarTickets} style={{
            marginTop: 8, background: 'var(--sn-accion)', color: 'var(--sn-accion-texto)',
            border: 'none', borderRadius: 8, padding: '8px 20px', cursor: 'pointer',
          }}>Reintentar</button>
        </div>
      ) : (
        <>
          {/* Tarjetas resumen */}
          {resumen && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, padding: '14px 16px 0' }}>
              {[
                { label: 'Abiertos', valor: resumen.abiertos, color: 'var(--sn-exito)', bg: 'var(--sn-exito-suave)', filtro: 'abierto' },
                { label: 'Respond.', valor: resumen.respondidos, color: 'var(--sn-marca)', bg: 'var(--sn-marca-suave)', filtro: 'respondido' },
                { label: 'Cerrados', valor: resumen.cerrados, color: 'var(--sn-texto-3)', bg: 'var(--sn-superficie-3)', filtro: 'cerrado' },
              ].map(card => (
                <div key={card.label} onClick={() => setFiltro(filtro === card.filtro as any ? 'todos' : card.filtro as any)}
                  style={{
                    background: card.bg, borderRadius: 14, padding: '12px 8px',
                    textAlign: 'center', cursor: 'pointer',
                    border: `2px solid ${filtro === card.filtro ? card.color : 'transparent'}`,
                    transition: 'border 0.2s',
                  }}>
                  <div style={{ fontSize: 26, fontWeight: 800, color: card.color }}>{card.valor}</div>
                  <div style={{ fontSize: 11, color: 'var(--sn-texto-2)', marginTop: 2 }}>{card.label}</div>
                </div>
              ))}
            </div>
          )}

          {/* Buscador */}
          <div style={{ padding: '12px 16px 0' }}>
            <div style={{ position: 'relative' }}>
              <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--sn-texto-3)' }}>🔍</span>
              <input
                value={busqueda}
                onChange={e => setBusqueda(e.target.value)}
                placeholder="Buscar por asunto, solicitante..."
                style={{
                  width: '100%', padding: '10px 12px 10px 36px',
                  border: '1.5px solid var(--sn-borde-fuerte)', borderRadius: 12,
                  fontSize: 13, outline: 'none', background: 'var(--sn-superficie)',
                  color: 'var(--sn-texto)',
                  boxSizing: 'border-box',
                }}
              />
            </div>
          </div>

          {/* Tabs */}
          <div style={{ display: 'flex', gap: 6, padding: '10px 16px', overflowX: 'auto' }}>
            {(['todos', 'abierto', 'respondido'] as const).map(f => (
              <button key={f} onClick={() => { setFiltro(f); setCerradosDesbloqueados(false); }} style={{
                padding: '6px 14px', borderRadius: 20, border: 'none', cursor: 'pointer',
                fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
                background: filtro === f ? 'var(--sn-accion)' : 'var(--sn-superficie-3)',
                color: filtro === f ? 'var(--sn-accion-texto)' : 'var(--sn-texto-2)',
                transition: 'all 0.2s',
              }}>
                {f === 'todos' ? 'Todos' : estadoLabel[f]}
                {f !== 'todos' && resumen && (
                  <span style={{ marginLeft: 6, opacity: 0.8 }}>
                    {f === 'abierto' ? resumen.abiertos : resumen.respondidos}
                  </span>
                )}
              </button>
            ))}
            {/* Tab cerrado con candado */}
            <button onClick={() => cerradosDesbloqueados ? setFiltro(filtro === 'cerrado' ? 'todos' : 'cerrado') : abrirCaptcha()} style={{
              padding: '6px 14px', borderRadius: 20, border: 'none', cursor: 'pointer',
              fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
              background: filtro === 'cerrado' ? 'var(--sn-info)' : 'var(--sn-superficie-3)',
              color: filtro === 'cerrado' ? 'var(--sn-texto-invertido)' : 'var(--sn-texto-2)',
              transition: 'all 0.2s',
              display: 'flex', alignItems: 'center', gap: 4,
            }}>
              {cerradosDesbloqueados ? '🔓' : '🔒'} Cerrado
              {resumen && <span style={{ opacity: 0.8 }}>{resumen.cerrados}</span>}
            </button>
          </div>

          {/* Banner cuando cerrados están bloqueados */}
          {!cerradosDesbloqueados && (
            <div style={{ margin: '0 16px 10px', background: 'var(--sn-superficie-2)', borderRadius: 12, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12, border: '1.5px dashed var(--sn-borde)' }}>
              <span style={{ fontSize: 28 }}>🔒</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--sn-texto-2)' }}>Historial cerrado oculto</div>
                <div style={{ fontSize: 11, color: 'var(--sn-texto-3)' }}>{resumen?.cerrados || 0} tickets · toca 🔒 para verlos</div>
              </div>
              <button onClick={abrirCaptcha} style={{
                background: 'var(--sn-accion)', color: 'var(--sn-accion-texto)', border: 'none',
                borderRadius: 8, padding: '6px 12px', fontSize: 11, fontWeight: 700, cursor: 'pointer',
              }}>Ver</button>
            </div>
          )}

          {/* Modal CAPTCHA */}
          {showCaptcha && (
            <div style={{
              position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999,
            }}>
              <div style={{
                background: 'var(--sn-superficie)', borderRadius: 20, padding: '28px 24px',
                width: 300, textAlign: 'center', boxShadow: 'var(--sn-sombra)',
              }}>
                <div style={{ fontSize: 36, marginBottom: 6 }}>🤖</div>
                <div style={{ fontWeight: 800, fontSize: 16, color: 'var(--sn-texto)', marginBottom: 4 }}>Verificación rápida</div>
                <div style={{ fontSize: 12, color: 'var(--sn-texto-3)', marginBottom: 20 }}>Resuelve para ver los tickets cerrados</div>

                {/* Pregunta */}
                <div style={{ background: 'var(--sn-superficie-2)', borderRadius: 14, padding: '16px 12px', marginBottom: 20 }}>
                  <div style={{ fontSize: 13, color: 'var(--sn-texto-2)', marginBottom: 8 }}>¿Cuánto es?</div>
                  <div style={{ fontSize: 28, letterSpacing: 4 }}>
                    {Array(captcha.a).fill(captcha.emoji).join('')}
                  </div>
                  <div style={{ fontSize: 20, color: 'var(--sn-marca-texto)', fontWeight: 800, margin: '6px 0' }}>+</div>
                  <div style={{ fontSize: 28, letterSpacing: 4 }}>
                    {Array(captcha.b).fill(captcha.emoji).join('')}
                  </div>
                </div>

                {/* Error */}
                {captchaError && (
                  <div style={{ color: 'var(--sn-peligro)', fontSize: 12, marginBottom: 10, fontWeight: 600 }}>
                    ❌ Incorrecto, intenta de nuevo
                  </div>
                )}

                {/* Opciones */}
                <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginBottom: 16 }}>
                  {captcha.opciones.map(op => (
                    <button key={op} onClick={() => responderCaptcha(op)} style={{
                      width: 64, height: 64, borderRadius: 14, border: '2px solid var(--sn-borde-fuerte)',
                      background: 'var(--sn-superficie)', fontSize: 22, fontWeight: 800, cursor: 'pointer',
                      color: 'var(--sn-marca-texto)', transition: 'all 0.15s',
                    }}>
                      {op}
                    </button>
                  ))}
                </div>

                <button onClick={() => setShowCaptcha(false)} style={{
                  background: 'none', border: 'none', color: 'var(--sn-texto-3)',
                  fontSize: 12, cursor: 'pointer', textDecoration: 'underline',
                }}>Cancelar</button>
              </div>
            </div>
          )}

          {/* Lista */}
          <div style={{ padding: '4px 16px 24px' }}>
            {ticketsFiltrados.length === 0 && (
              <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--sn-texto-3)' }}>
                <div style={{ fontSize: 40 }}>🎉</div>
                <p style={{ marginTop: 8 }}>No hay tickets en esta categoría</p>
              </div>
            )}
            {ticketsFiltrados.map(t => {
              const color = estadoColor[t.estado] || 'var(--sn-texto-3)';
              const fondoEstado = estadoFondo[t.estado] || 'var(--sn-superficie-3)';
              const textoEstado = estadoTexto[t.estado] || 'var(--sn-texto-2)';
              const dpBg = dpColor[t.dp] || 'var(--sn-superficie-2)';
              const ultimoMensaje = t.mensajes[t.mensajes.length - 1];
              return (
                <div key={t.id} onClick={() => setTicketSeleccionado(t)}
                  style={{
                    background: 'var(--sn-superficie)', borderRadius: 14, marginBottom: 10,
                    boxShadow: 'var(--sn-sombra)', cursor: 'pointer',
                    display: 'flex', overflow: 'hidden',
                    transition: 'transform 0.1s',
                  }}>
                  {/* Barra lateral de color */}
                  <div style={{ width: 5, background: color, flexShrink: 0 }} />

                  <div style={{ padding: '12px 14px', flex: 1, minWidth: 0 }}>
                    {/* Fila superior */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--sn-texto)', marginBottom: 2,
                          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {t.asunto}
                        </div>
                      </div>
                      <div style={{ fontSize: 10, color: 'var(--sn-texto-3)', whiteSpace: 'nowrap', flexShrink: 0 }}>
                        {fechaRelativa(t.lastdate)}
                      </div>
                    </div>

                    {/* Badges */}
                    <div style={{ display: 'flex', gap: 6, marginBottom: 6, flexWrap: 'wrap' }}>
                      <span style={{
                        fontSize: 9, fontWeight: 700, padding: '2px 8px', borderRadius: 20,
                        background: dpBg, color: 'var(--sn-texto)',
                      }}>{t.dp}</span>
                      <span style={{
                        fontSize: 9, fontWeight: 700, padding: '2px 8px', borderRadius: 20,
                        background: fondoEstado, color: textoEstado,
                      }}>{estadoLabel[t.estado]}</span>
                      <span style={{ fontSize: 9, color: 'var(--sn-texto-3)', padding: '2px 0' }}>#{t.id}</span>
                    </div>

                    {/* Preview último mensaje */}
                    {ultimoMensaje && (
                      <div style={{ fontSize: 11, color: 'var(--sn-texto-3)', display: 'flex', alignItems: 'center', gap: 4 }}>
                        <span>{ultimoMensaje.idtecnico > 0 ? '🔧' : '👤'}</span>
                        <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1 }}>
                          {decodeHTML(ultimoMensaje.contenido).substring(0, 80)}
                        </span>
                        {ultimoMensaje.adjunto ? <span>📎</span> : null}
                      </div>
                    )}

                    {/* Solicitante */}
                    {t.solicitante && (
                      <div style={{ fontSize: 10, color: 'var(--sn-texto-3)', marginTop: 4 }}>
                        Por: {t.solicitante}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
      </div>
      <BottomNav />
    </div>
  );
};

export default Tickets;
